import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSession } from '../session-context';
import { Copy, LogOut, Users, Shield, Download } from 'lucide-react';
import { useToast } from '../@/components/ui/use-toast';
import RoleBadge from './role-badge';

const SessionHeader: React.FC = () => {
  const {
    sessionId,
    gameState,
    leaveSession,
    activeUsers,
    userId,
    isDM,
    canClaimDM,
    claimDMRole,
    releaseDMRole
  } = useSession();
  const { toast } = useToast();
  const [showUsers, setShowUsers] = useState(false);
  const [claimingDM, setClaimingDM] = useState(false);
  
  if (!sessionId) return null;
  
  const copySessionId = () => {
    navigator.clipboard.writeText(sessionId);
    toast({
      title: 'Session ID copied',
      description: 'You can now share this ID with others to join your session',
    });
  };

  const handleClaimDM = async () => {
    setClaimingDM(true);
    try {
      const success = await claimDMRole();
      if (success) {
        toast({
          title: 'Role Updated',
          description: 'You are now the Dungeon Master for this session',
        });
      } else {
        toast({
          title: 'Unable to Claim Role',
          description: 'Another user has already claimed the DM role',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update role',
        variant: 'destructive',
      });
    } finally {
      setClaimingDM(false);
    }
  };

  const handleExport = () => {
    if (!gameState) {
      toast({
        title: 'Nothing to export',
        description: 'Session state has not loaded yet. Try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      app: 'stormwrack',
      version: 1,
      exportedAt: Date.now(),
      gameState,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stormwrack-session-${sessionId?.slice(0, 8) ?? 'export'}-${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Session exported',
      description: 'Saved the current session state to a JSON file.',
    });
  };

  const handleReleaseDM = async () => {
    try {
      await releaseDMRole();
      toast({
        title: 'Role Updated',
        description: 'You are now a Player in this session',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update role',
        variant: 'destructive',
      });
    }
  };
  
  const activeUserCount = Object.keys(activeUsers).length;
  const currentUserRole = activeUsers[userId]?.role || 'Player';
  
  return (
    <div className="flex justify-between items-center mb-4 px-4 py-2 bg-muted rounded-md">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="sm" onClick={copySessionId}>
          <Copy className="h-4 w-4 mr-2" />
          Session ID: {sessionId.substring(0, 8)}...
        </Button>

        <div className="flex items-center space-x-2 ml-2">
          <RoleBadge role={currentUserRole} />
          
          {currentUserRole !== 'DM' && canClaimDM() && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClaimDM}
              disabled={claimingDM}
              className="flex items-center gap-1"
            >
              <Shield className="h-4 w-4" />
              {claimingDM ? 'Claiming...' : 'Claim DM Role'}
            </Button>
          )}
          
          {currentUserRole === 'DM' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleReleaseDM}
              className="flex items-center gap-1"
            >
              Release DM Role
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <div className="relative">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowUsers(!showUsers)}
            className="flex items-center"
          >
            <Users className="h-4 w-4 mr-1" />
            <span>{activeUserCount} active</span>
          </Button>
          
          {/* User list dropdown */}
          {showUsers && (
            <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 p-2">
              <p className="font-semibold p-2">Active Users:</p>
              <ul className="space-y-1">
                {Object.entries(activeUsers).map(([id, { name, role }]) => (
                  <li key={id} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md flex items-center justify-between">
                    <span>{name}</span>
                    <RoleBadge role={role} showLabel={false} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {isDM() && (
          <Button variant="ghost" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        )}

        <Button variant="ghost" size="sm" onClick={leaveSession}>
          <LogOut className="h-4 w-4 mr-2" />
          Leave Session
        </Button>
      </div>
    </div>
  );
};

export default SessionHeader;